'use client'

import { authFetch } from '@/lib/auth/auth-fetch'
import { useWallet } from '@/providers/WalletContext'
import { useCallback, useEffect, useState } from 'react'

interface UserProfile {
	id: string
	email: string | null
	username: string | null
	avatarLink: string | null
	polymarketSafe: string | null
}

export function useUserProfile() {
	const { authenticated } = useWallet()

	const [profile, setProfile] = useState<UserProfile | null>(null)
	const [isLoading, setIsLoading] = useState(false)
	const [isSaving, setIsSaving] = useState(false)
	const [error, setError] = useState<string | null>(null)

	const fetchProfile = useCallback(async () => {
		if (!authenticated) return

		setIsLoading(true)
		setError(null)

		try {
			const res = await authFetch('/api/user/profile', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({}),
			})
			if (!res.ok) return

			const data = await res.json()
			setProfile(data.user)
		} catch {
			// Silently fail on fetch — profile is optional
		} finally {
			setIsLoading(false)
		}
	}, [authenticated])

	useEffect(() => {
		if (authenticated) {
			fetchProfile()
		} else {
			setProfile(null)
		}
	}, [authenticated, fetchProfile])

	const updateProfile = useCallback(
		async ({ username, avatar }: { username?: string; avatar?: File }) => {
			if (!authenticated) {
				setError('Not authenticated')
				return false
			}

			setIsSaving(true)
			setError(null)

			try {
				const formData = new FormData()

				if (username !== undefined) {
					formData.append('username', username)
				}
				if (avatar) {
					formData.append('avatar', avatar)
				}

				const res = await authFetch('/api/user/profile', {
					method: 'POST',
					body: formData,
				})

				const data = await res.json()

				if (!res.ok) {
					setError(data.error || 'Failed to update profile')
					return false
				}

				setProfile(prev =>
					prev
						? {
								...prev,
								username: data.user.username ?? prev.username,
								avatarLink: data.user.avatarLink ?? prev.avatarLink,
							}
						: prev
				)

				return true
			} catch {
				setError('Failed to update profile')
				return false
			} finally {
				setIsSaving(false)
			}
		},
		[authenticated]
	)

	return {
		profile,
		isLoading,
		isSaving,
		error,
		updateProfile,
		refetch: fetchProfile,
	}
}
